import React, {
  DragEvent,
  FC,
  PropsWithChildren,
  useCallback,
  useEffect,
  useState,
} from 'react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  AvatarBadge,
  Box,
  Button,
  ButtonGroup,
  ButtonProps,
  Collapse,
  Flex,
  Heading,
  HStack,
  IconButton,
  ListItem,
  OrderedList,
  Spacer,
  Spinner,
  Stack,
  Text,
  useColorModeValue,
  useDisclosure,
  VStack,
} from '@cardboard-ui/react';
import { t } from '@lingui/macro';
import {
  AddUserIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  InfoIcon,
  TrashIcon,
} from 'components/icons';
import { PDFTronFileRenderer } from '../PDFTronFileRenderer';
import { UserHandle } from 'components/UserHandle';
import { useOpenItemViewer } from '../../useOpenItemViewer';
import { ConfirmSignatureModal } from './ConfirmSignatureModal';
import { loadImage } from 'components/Editor/utils';
import { REQUEST_SIGNATURE_PARAM } from 'utils/routes';
import { graphql, useFragment, useSafeRelay } from 'utils/graphClient';
import { PrepareSignatureFileRenderer_node$key } from './__generated__/PrepareSignatureFileRenderer_node.graphql';
import { Layout, useActiveLayout } from 'utils/LayoutProvider';
import { AddMembersOverlay } from 'components/AddMembersOverlay';
import ConfirmationDialog from 'components/dialogs/ConfirmationDialog';
import differenceBy from 'lodash/differenceBy';
import isEqual from 'lodash/isEqual';
import uniq from 'lodash/uniq';
import { Member } from 'types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { light, solid } from '@fortawesome/fontawesome-svg-core/import.macro';
import { notEmpty } from 'utils/NotEmptyFilter';
import { useFeatureFlag } from 'utils/sessionProvider';
import { postMessageWTC } from '../utils';
import { FeedbackModal } from 'components/FeedbackModal';
import { createIcon } from 'components/icons/createIcon';
import { ConfirmCloseDialog } from './ConfirmCloseDialog';
import { UnsavedSignatureSetupDialog } from './UnsavedSignatureSetupDialog';
import { PrepareSignatureFileRendererQuery } from './__generated__/PrepareSignatureFileRendererQuery.graphql';
import { fileNodeFragment } from '..';
import { FileRenderer_node$key } from '../__generated__/FileRenderer_node.graphql';

interface PrepareSignatureFileRendererProps {
  node: PrepareSignatureFileRenderer_node$key;
}

const prepareSignatureFragment = graphql`
  fragment PrepareSignatureFileRenderer_node on Node {
    __typename
    id
    ... on FolderItem {
      space {
        memberships {
          edges {
            node {
              member {
                id
                name
                email
                avatar {
                  name
                  url
                }
                ...UserHandle_member
              }
            }
          }
        }
      }
      content {
        ... on File {
          id
          name
          url
          size
          mimeType
        }
      }
    }
    ... on VaultItem {
      vault {
        id
        members {
          id
          name
          email
          avatar {
            name
            url
          }
          ...UserHandle_member
        }
        canUpdate {
          value
        }
      }
      content {
        ... on File {
          id
          name
          url
          size
          mimeType
        }
      }
    }
    ... on SignableInterface {
      isSignable
      canCreateSignatureEnvelope {
        value
      }
      signatureEnvelope {
        id
        status
      }
    }
  }
`;

const ColorGenerator = {
  colors: [
    '#4299E1', // Blue.400
    '#F56565', // red.400
    '#ECC94B', // Yellow.400
    '#48BB78', // Green.400
    '#ED8936', // Orange.400
    '#0BC5EA', // Cyan.400
    '#9F7AEA', // Purple.400
    '#ED64A6', // Pink.400
    '#38B2AC', // Teal.400
  ],
  i: 0,
  next: () => {
    const value =
      ColorGenerator.colors[ColorGenerator.i % ColorGenerator.colors.length];
    ColorGenerator.i++;
    return {
      value,
      done: false,
    };
  },
  rewind: () => {
    ColorGenerator.i = 0;
  },
};

type Signer = {
  signerId: never;
  member: Member;
  color: string;
};

const memberToSigner = ({ member }: { member: Member }) =>
  ({
    signerId: `sig-${member.id}`,
    member: member,
    color: ColorGenerator.next().value,
  } as Signer);

const MAX_INITIAL_SIGNERS = 1;

export const PrepareSignatureFileRenderer: FC<{ nodeId: string }> = ({
  nodeId,
}) => {
  const { useLazyLoadQuery } = useSafeRelay();
  const data = useLazyLoadQuery<PrepareSignatureFileRendererQuery>(
    graphql`
      query PrepareSignatureFileRendererQuery($id: ID!) {
        node(id: $id) {
          ...PrepareSignatureFileRenderer_node
          ...useNodeSecurity_node
          ...FileRenderer_node
        }
      }
    `,
    { id: nodeId },
  );

  if (!data.node) {
    throw new Error('Node not found for PrepareSignatureFileRenderer');
  }

  const node = data.node;
  const {
    id,
    canCreateSignatureEnvelope,
    content,
    vault,
    space,
    signatureEnvelope,
  } = useFragment(
    prepareSignatureFragment,
    node as PrepareSignatureFileRenderer_node$key,
  );
  const fileNode = useFragment(fileNodeFragment, node as FileRenderer_node$key);

  useEffect(ColorGenerator.rewind, []);

  const { openItemViewer } = useOpenItemViewer();

  const {
    isOpen: isConfirmSignatureOpen,
    onClose: onConfirmSignatureClose,
    onOpen: onConfirmSignatureOpen,
  } = useDisclosure();
  const {
    isOpen: isAddSignerOpen,
    onClose: onAddSignerClose,
    onOpen: onAddSignerOpen,
  } = useDisclosure();
  const {
    isOpen: isSignatureFeedbackOpen,
    onClose: onSignatureFeedbackClose,
    onOpen: onSignatureFeedbackOpen,
  } = useDisclosure();
  const {
    isOpen: isUnsavedSignatureSetupDialogOpen,
    onClose: onUnsavedSignatureSetupDialogClose,
    onOpen: onUnsavedSignatureSetupDialogOpen,
  } = useDisclosure();

  const mutedTextColor = useColorModeValue('gray.500', 'gray.300');

  const spaceMembers = space?.memberships
    ? space.memberships.edges.map((e) => e.node?.member).filter(notEmpty)
    : [];

  const possibleSigners = vault?.members ? [...vault.members] : spaceMembers;

  const initialSelectedSigners =
    possibleSigners.length <= MAX_INITIAL_SIGNERS
      ? possibleSigners
          .slice(0, MAX_INITIAL_SIGNERS)
          .map((member) => memberToSigner({ member }))
      : [];

  const [selectedSigners, setSelectedSigners] = useState<Signer[]>(
    initialSelectedSigners,
  );

  const [isDocumentLoaded, setIsDocumentLoaded] = useState(false);
  const [isDocumentPreparable, setIsDocumentPreparable] = useState<
    'UNKNOWN' | 'PREPARABLE' | 'UNPREPARABLE'
  >('UNKNOWN');
  const [isDocumentDirty, setIsDocumentDirty] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [dropPageX, setDropPageX] = useState(0);
  const [dropPageY, setDropPageY] = useState(0);

  const isDesktop = useActiveLayout() === Layout.DESKTOP;
  const isSignatureActive = signatureEnvelope?.status === 'SIGNING';
  const isSignatureCompleted = signatureEnvelope?.status === 'COMPLETED';

  const loading = isDocumentPreparable === 'UNKNOWN';

  const canPrepare = {
    isDesktop: isDesktop,
    isAdmin: !!canCreateSignatureEnvelope?.value,
    isUsers: possibleSigners.length >= 1,
    isDocumentPreparable: isDocumentPreparable === 'PREPARABLE',
    isEmptySignature: !isSignatureActive && !isSignatureCompleted,
  };

  const canPrepareSignatureFileRenderer =
    Object.values(canPrepare).every(Boolean);

  const [dragUserImage, setDragUserImage] = useState<HTMLImageElement>();
  const [appliedSignerIds, setAppliedSignerIds] = useState<string[]>([]);

  const observeWebViewer = useCallback((event: any) => {
    if (event.data.type === 'crdbrd-ctow-documentLoaded') {
      setIsDocumentLoaded(true);
    }
    if (event.data.type === 'crdbrd-ctow-documentPrepareable') {
      setIsDocumentPreparable(
        (event.data.documentPreparable as boolean)
          ? 'PREPARABLE'
          : 'UNPREPARABLE',
      );
    }

    if (event.data.type === 'crdbrd-ctow-annotationChanged') {
      setAppliedSignerIds(uniq(event.data.appliedSignerIds as string[]));
      setIsDocumentDirty(event.data.isDocumentDirty);
    }
  }, []);

  // Listen to incoming messages for the webviewer
  useEffect(() => {
    window.addEventListener('message', observeWebViewer);
    return () => window.removeEventListener('message', observeWebViewer);
  }, [observeWebViewer]);

  // Create an image based drag handler on a canvas
  // Which is used in dargStart
  useEffect(() => {
    const labelCanvasURL = drawDragCanvas();
    loadImage(labelCanvasURL).then((img) => {
      setDragUserImage(img as HTMLImageElement);
    });
  }, []);

  // When dragging starts do
  // * Keep track if dragging is happening
  // * Attach the image created in  a previous hook to the drag handeler
  const dragStart = (e: DragEvent<HTMLButtonElement>, signer: Signer) => {
    setIsDragging(true);

    if (dragUserImage) {
      e.dataTransfer.setDragImage(dragUserImage, 0, 0);
    }
  };

  const dragEnd = (
    e: DragEvent<HTMLButtonElement>,
    type: FieldType,
    signer: Signer, // Is a member
  ) => {
    e.preventDefault();
    const webViewerBoundingClientRect = document
      .getElementById('web-viewer')
      ?.querySelector('iframe')
      ?.getBoundingClientRect();
    const offsetX = webViewerBoundingClientRect?.x || 0;
    const offsetY = webViewerBoundingClientRect?.y || 0;

    const pagePoint = { x: dropPageX - offsetX, y: dropPageY - offsetY };

    let value = '';
    let fieldName = `${signer.member.name} <${signer.member.email}>`;
    switch (type) {
      case 'LABEL_NAME':
        value = signer.member.name || '';
        break;
      case 'LABEL_EMAIL':
        value = signer.member.email || '';
        break;
      case 'LABEL_TEXT':
        value = 'Custom label';
        break;
      case 'SIGNATURE_DRAW':
        value = 'Signature';
        fieldName = `${fieldName} Signature`;
        break;
      case 'SIGNATURE_INITIALS':
        value = 'Initials';
        fieldName = `${fieldName} Initials`;
        break;
      case 'INPUT_TEXT':
        value = 'Text input';
        fieldName = `${fieldName} Text input`;
        break;
      case 'INPUT_SIGNATURE_DATE':
        value = 'Signature Date';
        fieldName = `${fieldName} Signature Date`;
        break;
    }

    // Send message to Webviewer to actually add the field
    addField({
      fieldType: type,
      pagePoint,
      signerId: signer.signerId,
      fieldName,
      value,
      color: signer.color,
    });

    // Reset local states after dropping
    setIsDragging(false);
    setDropPageX(0);
    setDropPageY(0);
  };

  const tryClosePrepareSignature = () => {
    if (isDocumentDirty) {
      onUnsavedSignatureSetupDialogOpen();
    } else {
      closePrepareSignature();
    }
  };

  const closePrepareSignature = () => {
    openItemViewer(id, {
      [REQUEST_SIGNATURE_PARAM]: undefined,
    });
  };

  const removeSigner = (signerId: string) => {
    removeSignerFields(signerId);

    const _selectedSigners = selectedSigners.filter(
      (s) => s.signerId !== signerId,
    );
    setSelectedSigners(_selectedSigners);
  };

  return (
    <>
      <HStack spacing={0} flex={1}>
        {isDocumentLoaded && (
          <VStack
            p={3}
            h="100%"
            width={96}
            alignItems="flex-start"
            justifyContent="space-between"
            bg="gray.50"
            _dark={{ bg: 'gray.800' }}
            data-qa="prepare-signature-sidebar"
          >
            <Stack overflowY="auto" width="100%">
              {canPrepareSignatureFileRenderer ? (
                <VStack spacing={4}>
                  {selectedSigners.map((signer) => (
                    <SignerConfiguration
                      key={signer.signerId}
                      signer={signer}
                      appliedMemberIds={appliedSignerIds}
                      dragStart={dragStart}
                      dragEnd={dragEnd}
                      removeSigner={removeSigner}
                    />
                  ))}
                  <Button
                    width="100%"
                    size="sm"
                    rightIcon={<AddUserIcon />}
                    onClick={onAddSignerOpen}
                    isDisabled={selectedSigners.length >= 64}
                  >
                    {t`Add Signer`}
                  </Button>
                </VStack>
              ) : loading ? (
                <Spinner />
              ) : (
                <CantPrepareSignatureAlert {...canPrepare} />
              )}
            </Stack>

            {canPrepareSignatureFileRenderer && (
              <Box mt={3} pb={3} width="100%">
                <ButtonGroup mt={3} size="sm" width="100%">
                  <Button
                    width="50%"
                    colorScheme="gray"
                    onClick={tryClosePrepareSignature}
                    rightIcon={<TrashIcon />}
                  >{t`Cancel`}</Button>
                  <Button
                    width="50%"
                    colorScheme="green"
                    onClick={onConfirmSignatureOpen}
                    rightIcon={<CheckIcon />}
                    isDisabled={
                      selectedSigners.length === 0 ||
                      !isEqual(
                        selectedSigners.map(({ signerId }) => signerId).sort(),
                        appliedSignerIds.sort(),
                      )
                    }
                  >{t`Request`}</Button>
                </ButtonGroup>
                <FeedbackModal
                  title={t`Digital signatures`}
                  isOpen={isSignatureFeedbackOpen}
                  onClose={onSignatureFeedbackClose}
                />
              </Box>
            )}
          </VStack>
        )}

        <Flex
          flex={1}
          h="100%"
          onDrop={(e) => {
            setDropPageX(e.pageX);
            setDropPageY(e.pageY);
          }}
          data-qa="prepare-signature-web-viewer"
        >
          {isDragging && <TransparentPlaceholder />}
          <PDFTronFileRenderer node={fileNode} mode="prepareSignature" />
        </Flex>
      </HStack>

      <AddMembersOverlay
        isOpen={isAddSignerOpen}
        onClose={onAddSignerClose}
        loading={false}
        onSubmit={({ selectedMemberIds }) => {
          return new Promise((resolve) => {
            setSelectedSigners((state) => {
              const currentIds = state.map(({ signerId }) => signerId);
              (selectedMemberIds || [])
                .map((selectedId) =>
                  possibleSigners.find(({ id }) => id === selectedId),
                )
                .filter((s) => s)
                .map((m) => memberToSigner({ member: m as Member }))
                .forEach((signer) => {
                  if (!currentIds.includes(signer.signerId)) {
                    state.push(signer);
                  }
                });

              return state;
            });
            resolve();
          });
        }}
        usersInitiallyAvailableForAddition={differenceBy(
          possibleSigners,
          selectedSigners,
          'id',
        )}
        disableEmailInvite
        // TODO: multiple selection can surpass limit of 64, should we allow select all?
        allowSelectAll
        selectAllCTA={t`Select all available members`}
      />

      <ConfirmSignatureModal
        isOpen={isConfirmSignatureOpen}
        onClose={onConfirmSignatureClose}
        onComplete={closePrepareSignature}
        appliedMemberIds={appliedSignerIds}
        currentFileUrl={content?.url || ''}
        nodeId={id}
      />

      <ConfirmCloseDialog isDocumentDirty={isDocumentDirty} />

      <UnsavedSignatureSetupDialog
        isOpen={isUnsavedSignatureSetupDialogOpen}
        onClose={onUnsavedSignatureSetupDialogClose}
        onAction={closePrepareSignature}
      />
    </>
  );
};

type FieldData = {
  fieldType: string;
  fieldName: string;
  pagePoint: { x: number; y: number };
  signerId: string;
  value?: string;
  color: string;
};

const addField = (fieldData: FieldData) => {
  postMessageWTC({
    type: 'addField',
    fieldData,
  });
};

const removeSignerFields = (memberId: string) => {
  postMessageWTC({
    type: 'removeSignerFields',
    signerId: memberId,
  });
};

const drawDragCanvas = () => {
  const canvas = document.createElement('canvas');
  canvas.width = 200;
  canvas.height = 40;
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.beginPath();
    ctx.rect(0, 0, 200, 40);
    ctx.lineWidth = 3;
    ctx.stroke();
  }
  const canvasURL = canvas.toDataURL();
  return canvasURL;
};

const TransparentPlaceholder = () => (
  <div
    style={{
      position: 'absolute',
      backgroundColor: 'transparent',
      height: '100%',
      width: '100%',
    }}
    onDragOver={(e) => {
      e.preventDefault();
    }}
  />
);

const ValidIcon = createIcon(solid('check'));
const InvalidIcon = createIcon(solid('times'));
const ValidityIcon = ({ isValid }: { isValid: boolean }) =>
  isValid ? <ValidIcon color="green.500" /> : <InvalidIcon color="red.500" />;

interface CantPrepareSignatureAlertProps {
  isDesktop: boolean;
  isAdmin: boolean;
  isUsers: boolean;
  isDocumentPreparable: boolean;
  isEmptySignature: boolean;
}

const CantPrepareSignatureAlert = ({
  isDesktop,
  isAdmin,
  isUsers,
  isDocumentPreparable,
  isEmptySignature,
}: CantPrepareSignatureAlertProps) => {
  return (
    <Alert
      status="info"
      variant="subtle"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      p={6}
      borderRadius="lg"
    >
      <InfoIcon size="3x" />
      <AlertTitle mt={4}>{t`You can't request signatures.`}</AlertTitle>
      <AlertDescription mt={4}>
        {t`Please make sure the followings are true:`}
        <OrderedList mt={1}>
          <ListItem>
            {t`You are editing from a desktop view.`}{' '}
            <ValidityIcon isValid={isDesktop} />
          </ListItem>
          <ListItem>
            {t`You have admin rights`} <ValidityIcon isValid={isAdmin} />
          </ListItem>
          <ListItem>
            {t`There is at least one member.`}{' '}
            <ValidityIcon isValid={isUsers} />
          </ListItem>
          <ListItem>
            {t`There is no signature or form field.`}{' '}
            <ValidityIcon isValid={isDocumentPreparable} />
          </ListItem>
          <ListItem>
            {t`There is no active or completed signature request.`}{' '}
            <ValidityIcon isValid={isEmptySignature} />
          </ListItem>
        </OrderedList>
      </AlertDescription>
    </Alert>
  );
};

interface SignerConfigurationOptions {
  signer: Signer;
  appliedMemberIds: string[];
  dragStart: (e: DragEvent<HTMLButtonElement>, signer: Signer) => void;
  dragEnd: (
    e: DragEvent<HTMLButtonElement>,
    type: FieldType,
    signer: Signer,
  ) => void;
  removeSigner: (memberId: string) => void;
}

const SignerConfiguration = ({
  signer,
  appliedMemberIds,
  dragStart,
  dragEnd,
  removeSigner,
}: SignerConfigurationOptions) => {
  const { isOpen, onToggle } = useDisclosure({ defaultIsOpen: true });
  const {
    isOpen: isConfirmationOpen,
    onClose: onConfirmationClose,
    onOpen: onConfirmationOpen,
  } = useDisclosure();
  const isSignaturesWithInputsDisabled = useFeatureFlag(
    'FEATURE_SIGNATURES_WITH_INPUTS_DISABLED',
  );
  const mutedTextColor = useColorModeValue('gray.500', 'gray.300');

  const isAdded = appliedMemberIds.includes(signer.signerId);
  const commonFieldProps = { dragStart, dragEnd, signer };

  return (
    <>
      <Stack
        bg="white"
        _dark={{ bg: 'gray.700' }}
        borderRadius="md"
        px={3}
        py={2}
        width="100%"
        spacing={0}
      >
        <HStack w="100%">
          <HStack w="100%" onClick={onToggle} cursor="pointer">
            <IconButton
              size="xs"
              icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
              aria-label={t`Collapse signer`}
              borderRadius="full"
              bgColor={signer.color}
              onClick={onToggle}
            />
            <UserHandle
              showAvatar
              avatarProps={{ size: 'xs' }}
              avatarChildren={
                isAdded ? (
                  <AvatarBadge boxSize="1.25em" bg="green.500" color="white">
                    <CheckIcon />
                  </AvatarBadge>
                ) : null
              }
              member={signer.member}
            />
            <Spacer />
          </HStack>
          <IconButton
            size="sm"
            variant="ghost"
            icon={<TrashIcon />}
            aria-label={t`Remove signer`}
            onClick={onConfirmationOpen}
          />
        </HStack>

        <Collapse animateOpacity in={isOpen}>
          <Stack spacing={4} pt={4}>
            <Stack>
              <Heading size="xs" fontWeight="normal">{t`Labels:`}</Heading>
              <Field
                fieldType="LABEL_NAME"
                icon={<FontAwesomeIcon icon={light('id-card')} />}
                {...commonFieldProps}
              >
                {signer.member.name}
              </Field>
              <Field
                fieldType="LABEL_EMAIL"
                icon={<FontAwesomeIcon icon={light('at')} />}
                {...commonFieldProps}
              >
                {signer.member.email}
              </Field>
              <Field
                fieldType="LABEL_TEXT"
                icon={<FontAwesomeIcon icon={light('font-case')} />}
                {...commonFieldProps}
              >{t`Custom label`}</Field>
            </Stack>

            <Stack>
              <HStack>
                <Heading size="xs" fontWeight="normal">{t`Fields:`}</Heading>
                <Spacer />
                <Text fontSize="sm" color={mutedTextColor}>
                  *{t`Mandatory`}
                </Text>
              </HStack>

              <Field
                fieldType="SIGNATURE_DRAW"
                icon={<FontAwesomeIcon icon={light('signature')} />}
                {...commonFieldProps}
              >
                {t`Signature`}*
              </Field>
              <Field
                fieldType="SIGNATURE_INITIALS"
                icon={<FontAwesomeIcon icon={light('signature')} />}
                {...commonFieldProps}
              >{t`Initials`}</Field>

              {!isSignaturesWithInputsDisabled && (
                <Field
                  fieldType="INPUT_TEXT"
                  icon={<FontAwesomeIcon icon={light('pen-field')} />}
                  {...commonFieldProps}
                >{t`Text input`}</Field>
              )}
              <Field
                fieldType="INPUT_SIGNATURE_DATE"
                icon={<FontAwesomeIcon icon={light('calendar-pen')} />}
                {...commonFieldProps}
              >{t`Signing Date (auto)`}</Field>
            </Stack>
          </Stack>
        </Collapse>
      </Stack>

      <ConfirmationDialog
        action={{
          name: t`Remove signer?`,
          onAction: () => removeSigner(signer.signerId),
        }}
        isOpen={isConfirmationOpen}
        onClose={onConfirmationClose}
      >
        {t`This will remove the signer from the list and all corresponding fields from the document.`}
      </ConfirmationDialog>
    </>
  );
};

type FieldType =
  | 'INPUT_SIGNATURE_DATE'
  | 'INPUT_TEXT'
  | 'SIGNATURE_INITIALS'
  | 'SIGNATURE_DRAW'
  | 'LABEL_TEXT'
  | 'LABEL_EMAIL'
  | 'LABEL_NAME';

interface FieldProps {
  icon: ButtonProps['rightIcon'];
  signer: Signer;
  fieldType: FieldType;
  dragStart: (e: any, signer: Signer) => void;
  dragEnd: (e: any, fieldType: FieldType, signer: Signer) => void;
}

const Field: FC<PropsWithChildren<FieldProps>> = ({
  children,
  dragStart,
  dragEnd,
  fieldType,
  signer,
  icon,
}) => (
  <Button
    draggable
    cursor="grab"
    width="100%"
    size="md"
    justifyContent="start"
    onDragStart={(e) => dragStart(e, signer)}
    onDragEnd={(e) => dragEnd(e, fieldType, signer)}
    leftIcon={icon}
    display="grid"
    gridTemplateColumns="auto 1fr auto"
    data-qa={`${fieldType}_BUTTON`}
  >
    <Box textAlign="start">{children}</Box>
    <Box>
      <FontAwesomeIcon icon={light('arrows-up-down-left-right')} />
    </Box>
  </Button>
);
